//
// Copyright (c) 2011-2019 by Curalate, Inc.
//

import React from 'react';

import { IContentLibraryItem } from '@apis/content-library/contentLibraryApiModel';
import ActionButton from '@components/atoms/action-button/ActionButton';
import Icon from '@components/atoms/icon/Icon';
import { ItemMetadata } from '@components/atoms/source/Source';
import VisuallyHiddenText from '@components/atoms/visually-hidden-text/VisuallyHiddenText';
import { SmallTooltip } from '@components/molecules/tooltip/Tooltip';
import { SocialNetwork } from '@models/SocialNetwork';

interface IOwnProps extends React.HTMLAttributes<HTMLDivElement> {
  itemInfo?: ItemMetadata;
  isUploaded?: boolean;
  isProduct?: boolean;
  ingestionSource?: IContentLibraryItem['ingestionSource'];
}

type Props = IOwnProps;

/**
 * The SourceAction atom renders an ActionButton
 * with an icon corresponding to the network type
 * of the provided item. When clicked, we open the
 * original post's URL in a new tab. If no supported
 * network is provided, this atom returns null. If
 * no originalUrl is provided, the button is disabled.
 *
 * The only supported network for now is Instagram
 */
const SourceActionButton: React.FunctionComponent<Props> = ({
  className,
  ingestionSource,
  isProduct,
  isUploaded,
  itemInfo: { originalUrl, socialNetwork } = {},
}) => {
  let sourceButton = null;

  if (socialNetwork === SocialNetwork.Instagram) {
    sourceButton = (
      <ActionButton
        className={className}
        disabled={!originalUrl}
        iconName='instagram'
        label={originalUrl && 'View on Instagram'}
      />
    );
  } else if (socialNetwork === SocialNetwork.TikTok) {
    sourceButton = (
      <ActionButton
        className={className}
        disabled={!originalUrl}
        iconName='tiktok'
        label={originalUrl && 'View on TikTok'}
      />
    );
  } else if (socialNetwork == SocialNetwork.YouTube) {
    sourceButton = (
      <ActionButton
        className={className}
        disabled={!originalUrl}
        iconName='youtube'
        label={originalUrl && 'View on YouTube'}
      />
    );
  } else if (socialNetwork === SocialNetwork.Bazaarvoice) {
    sourceButton = (
      <SmallTooltip
        className={className}
        content='Media was collected as part of a review.'
        isDisabled={false}
        placement='bottom'
        target={
          <>
            <Icon name='bv-logomark' />
            <VisuallyHiddenText>Bazaarvoice</VisuallyHiddenText>
          </>
        }
      />
    );
  } else if (isUploaded) {
    if (ingestionSource === 'Dropbox') {
      sourceButton = (
        <SmallTooltip
          className={className}
          content='This item was imported from Dropbox.'
          isDisabled={false}
          placement='bottom'
          target={
            <>
              <Icon name='dropbox' />
              <VisuallyHiddenText>Dropbox</VisuallyHiddenText>
            </>
          }
        />
      );
    } else {
      sourceButton = (
        <SmallTooltip
          className={className}
          content='This item was uploaded.'
          isDisabled={false}
          placement='bottom'
          target={
            <>
              <Icon name='upload' />
              <VisuallyHiddenText>Uploaded</VisuallyHiddenText>
            </>
          }
        />
      );
    }
  } else if (isProduct) {
    sourceButton = (
      <ActionButton
        className={className}
        disabled={!originalUrl}
        iconName='product-bag'
        label={originalUrl && 'View Product'}
      />
    );
  }
  // TODO: PD-246302 - Fix implementation so a button is not nested in an anchor
  return (
    sourceButton && (
      <a href={originalUrl} rel='noreferrer' target='_blank'>
        {sourceButton}
      </a>
    )
  );
};

export default SourceActionButton;
